import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDatabase } from '@/contexts/DatabaseContext';
import DashboardLayout from '@/components/feature/DashboardLayout';

const PROBLEM_TYPES = [
  'cameraDSMDecollee',
  'cameraADASDecollee',
  'cameraDSMMalReglee',
  'cameraADASMalReglee',
  'cameraFloue',
  'extractionImpossible',
  'pasCommunication',
  'perteSignalGPS',
  'videoDSMPerdue',
  'videoADASPerdue',
  'buzzerSonneArret',
  'buzzer30kmh',
  'buzzer40kmh',
  'buzzer60kmh',
  'buzzerNeSonnePas',
] as const;

function computeRepairStatus(general: string): 'OK' | 'En attente' | 'Terminé' {
  switch (general) {
    case 'Bon':
      return 'OK';
    case 'En panne':
      return 'En attente';
    case 'Réparé':
      return 'Terminé';
    default:
      return 'OK';
  }
}

export default function TruckDetail() {
  const { t } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { trucks, tasks, currentUser, drivers, truckComments, updateTruck, deleteTruck, addTruckComment, deleteTruckComment } = useDatabase();

  const truck = useMemo(() => trucks.find((tr) => tr.id === id), [trucks, id]);

  const [isEditing, setIsEditing] = useState(false);
  const [editedTruck, setEditedTruck] = useState(truck);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [newComment, setNewComment] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Comments filtered for this truck from db
  const truckCommentsForTruck = useMemo(
    () => truckComments.filter((c) => c.truckId === id).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ),
    [truckComments, id],
  );

  // Auto-enter edit mode when ?edit=true
  useEffect(() => {
    if (truck && searchParams.get('edit') === 'true') {
      setIsEditing(true);
      setEditedTruck(truck);
      const currentDriver = drivers.find(
        (d) => d.name === truck.driverName && d.contact === truck.driverContact
      );
      setSelectedDriverId(currentDriver ? currentDriver.id : '');
    }
  }, [truck, searchParams, drivers]);

  const truckTasks = useMemo(() => tasks.filter((task) => task.truckId === id), [tasks, id]);

  if (!truck) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center">
          <p className="text-foreground-500">{t('truckNotFound')}</p>
          <Link to="/" className="text-primary-600 hover:underline text-sm mt-2 inline-block">
            {t('backToDashboard')}
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const handleSave = () => {
    if (editedTruck) {
      updateTruck(truck.id, editedTruck);
      setIsEditing(false);
    }
  };

  const handleDelete = async () => {
    await deleteTruck(truck.id);
    navigate('/trucks');
  };

  const handleDriverSelect = (driverId: string) => {
    setSelectedDriverId(driverId);
    const driver = drivers.find((d) => d.id === driverId);
    if (driver) {
      setEditedTruck((prev) =>
        prev ? { ...prev, driverName: driver.name, driverContact: driver.contact } : prev
      );
    }
  };

  const handleGeneralStatusChange = (value: string) => {
    const general = value as 'Bon' | 'En panne' | 'Réparé';
    const newRepairStatus = computeRepairStatus(general);
    setEditedTruck((prev) =>
      prev ? { ...prev, generalStatus: general, repairStatus: newRepairStatus } : prev
    );
  };

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    addTruckComment({
      truckId: truck.id,
      userId: currentUser.id,
      userName: currentUser.fullName,
      content: newComment.trim(),
    });
    setNewComment('');
  };

  const handleDeleteComment = (commentId: string) => {
    deleteTruckComment(commentId);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'OK':
        return 'bg-primary-100 text-primary-800';
      case 'En attente':
        return 'bg-accent-100 text-accent-800';
      case 'Terminé':
        return 'bg-primary-100 text-primary-800';
      case 'Bon':
        return 'bg-primary-100 text-primary-800';
      case 'En panne':
        return 'bg-accent-100 text-accent-800';
      case 'Réparé':
        return 'bg-primary-100 text-primary-800';
      case 'En cours':
        return 'bg-secondary-100 text-secondary-800';
      default:
        return 'bg-background-100 text-foreground-600';
    }
  };

  const getGeneralStatusLabel = (status: string) => {
    switch (status) {
      case 'Bon':
        return t('good');
      case 'En panne':
        return t('broken');
      case 'Réparé':
        return t('repaired');
      default:
        return status;
    }
  };

  const getRepairStatusLabel = (status: string) => {
    switch (status) {
      case 'OK':
        return t('ok');
      case 'En attente':
        return t('waiting');
      case 'Terminé':
        return t('done');
      default:
        return status;
    }
  };

  const displayTruck = isEditing && editedTruck ? editedTruck : truck;

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <Link
              to="/"
              className="text-sm text-foreground-500 hover:text-foreground-800 flex items-center gap-1 mb-2"
            >
              <i className="ri-arrow-left-line" />
              {t('backToDashboard')}
            </Link>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              {truck.doorNumber}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">{truck.licensePlate}</p>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-save-line mr-1.5" />
                  {t('save')}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditedTruck(truck);
                    setSelectedDriverId('');
                  }}
                  className="px-4 py-2 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
                >
                  {t('cancel')}
                </button>
              </>
            ) : deleteConfirmOpen ? (
              <>
                <span className="text-sm text-foreground-600 self-center hidden sm:inline">
                  {t('confirmDeleteTruck')}
                </span>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-accent-500 text-background-50 rounded-lg text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  {t('delete')}
                </button>
                <button
                  onClick={() => setDeleteConfirmOpen(false)}
                  className="px-4 py-2 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
                >
                  {t('cancel')}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    setIsEditing(true);
                    setEditedTruck(truck);
                    const currentDriver = drivers.find(
                      (d) => d.name === truck.driverName && d.contact === truck.driverContact
                    );
                    setSelectedDriverId(currentDriver ? currentDriver.id : '');
                  }}
                  className="px-4 py-2 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap"
                >
                  <i className="ri-edit-line mr-1.5" />
                  {t('edit')}
                </button>
                <button
                  onClick={() => setDeleteConfirmOpen(true)}
                  title={t('deleteTruck')}
                  className="px-4 py-2 bg-background-100 text-accent-700 rounded-lg text-sm font-medium hover:bg-accent-50 transition-colors whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line mr-1.5" />
                  {t('delete')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          {/* Main info */}
          <div className="lg:col-span-2 bg-background-50 rounded-xl border border-background-200 p-4 md:p-6">
            <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-4">
              {t('truckDetails')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              {/* N° Portière — éditable */}
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('doorNumber')}</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedTruck?.doorNumber || ''}
                    onChange={(e) =>
                      setEditedTruck((prev) =>
                        prev ? { ...prev, doorNumber: e.target.value } : prev
                      )
                    }
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                ) : (
                  <p className="text-sm text-foreground-800 font-semibold">{displayTruck.doorNumber}</p>
                )}
              </div>

              {/* N° Immatriculation — éditable */}
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('licensePlate')}</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedTruck?.licensePlate || ''}
                    onChange={(e) =>
                      setEditedTruck((prev) =>
                        prev ? { ...prev, licensePlate: e.target.value } : prev
                      )
                    }
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                ) : (
                  <p className="text-sm text-foreground-800">{displayTruck.licensePlate}</p>
                )}
              </div>

              {/* État Général — 3 choix : Bon / En panne / Réparé */}
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('generalStatus')}</label>
                {isEditing ? (
                  <select
                    value={editedTruck?.generalStatus || 'Bon'}
                    onChange={(e) => handleGeneralStatusChange(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="Bon">{t('good')}</option>
                    <option value="En panne">{t('broken')}</option>
                    <option value="Réparé">{t('repaired')}</option>
                  </select>
                ) : (
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(displayTruck.generalStatus)}`}
                  >
                    {getGeneralStatusLabel(displayTruck.generalStatus)}
                  </span>
                )}
              </div>

              {/* Statut de réparation — lecture seule, auto-calculé */}
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('repairStatus')}</label>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(displayTruck.repairStatus)}`}
                >
                  {getRepairStatusLabel(displayTruck.repairStatus)}
                </span>
                {isEditing && (
                  <p className="text-[11px] text-foreground-400 mt-1">
                    Modifié automatiquement selon l'état général
                  </p>
                )}
              </div>

              {/* Problème signalé — liste déroulante */}
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('reportedProblem')}</label>
                {isEditing ? (
                  <select
                    value={editedTruck?.reportedProblem || ''}
                    onChange={(e) =>
                      setEditedTruck((prev) =>
                        prev ? { ...prev, reportedProblem: e.target.value } : prev
                      )
                    }
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="">{t('noProblem')}</option>
                    {PROBLEM_TYPES.map((key) => (
                      <option key={key} value={t(key)}>
                        {t(key)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-foreground-800">
                    {displayTruck.reportedProblem || '-'}
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('breakdownDate')}</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={editedTruck?.breakdownDate || ''}
                    onChange={(e) =>
                      setEditedTruck((prev) =>
                        prev ? { ...prev, breakdownDate: e.target.value } : prev
                      )
                    }
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                ) : (
                  <p className="text-sm text-foreground-800">
                    {displayTruck.breakdownDate
                      ? new Date(displayTruck.breakdownDate).toLocaleDateString('fr-FR')
                      : '-'}
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('repairsDone')}</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedTruck?.repairsDone || ''}
                    onChange={(e) =>
                      setEditedTruck((prev) =>
                        prev ? { ...prev, repairsDone: e.target.value } : prev
                      )
                    }
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                ) : (
                  <p className="text-sm text-foreground-800">
                    {displayTruck.repairsDone || '-'}
                  </p>
                )}
              </div>

              {/* Chauffeur — select dropdown avec auto-remplissage contact */}
              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('driver')}</label>
                {isEditing ? (
                  <select
                    value={selectedDriverId}
                    onChange={(e) => handleDriverSelect(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="">{t('selectDriver')}</option>
                    {drivers.map((driver) => (
                      <option key={driver.id} value={driver.id}>
                        {driver.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-foreground-800">{displayTruck.driverName || '-'}</p>
                )}
              </div>

              <div>
                <label className="text-xs text-foreground-500 mb-1 block">{t('contact')}</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedTruck?.driverContact || ''}
                    readOnly
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-700 cursor-not-allowed"
                  />
                ) : (
                  <p className="text-sm text-foreground-800">
                    {displayTruck.driverContact || '-'}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs text-foreground-500 mb-1 block">{t('comments')}</label>
                {isEditing ? (
                  <textarea
                    value={editedTruck?.comments || ''}
                    onChange={(e) =>
                      setEditedTruck((prev) =>
                        prev ? { ...prev, comments: e.target.value } : prev
                      )
                    }
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  />
                ) : (
                  <p className="text-sm text-foreground-800">
                    {displayTruck.comments || '-'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Tasks sidebar */}
          <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider">
                {t('tasks')}
              </h2>
              <button
                onClick={() => navigate('/tasks')}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                {t('view')}
              </button>
            </div>

            {truckTasks.length === 0 ? (
              <p className="text-sm text-foreground-500 py-4 text-center">{t('noTasks')}</p>
            ) : (
              <div className="space-y-3">
                {truckTasks.map((task) => (
                  <div
                    key={task.id}
                    className="p-3 rounded-lg bg-background-100 border border-background-200"
                  >
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-sm font-medium text-foreground-900">{task.title}</p>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          task.priority === 'Haute'
                            ? 'bg-accent-100 text-accent-800'
                            : task.priority === 'Moyenne'
                            ? 'bg-secondary-100 text-secondary-800'
                            : 'bg-primary-100 text-primary-800'
                        }`}
                      >
                        {task.priority}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-500 mb-2">{task.assignedToName}</p>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        task.status === 'Terminé'
                          ? 'bg-primary-100 text-primary-800'
                          : task.status === 'En cours'
                          ? 'bg-secondary-100 text-secondary-800'
                          : 'bg-accent-100 text-accent-800'
                      }`}
                    >
                      {task.status === 'À faire'
                        ? t('todo')
                        : task.status === 'En cours'
                        ? t('inProgress')
                        : t('done')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 md:p-6">
          <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-4">
            {t('comments')}
          </h2>

          {/* Add comment */}
          <div className="flex gap-3 mb-6">
            <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-700 font-semibold text-xs shrink-0">
              {currentUser.fullName.charAt(0)}
            </div>
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t('newComment')}
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-foreground-400">{newComment.length}/500</span>
                <button
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  className="px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {t('send')}
                </button>
              </div>
            </div>
          </div>

          {/* Comments list */}
          {truckCommentsForTruck.length === 0 ? (
            <p className="text-sm text-foreground-500 text-center py-4">{t('noComments')}</p>
          ) : (
            <div className="space-y-4">
              {truckCommentsForTruck.map((comment) => (
                <div key={comment.id} className="flex gap-3 group">
                  <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-700 font-semibold text-xs shrink-0">
                    {comment.userName.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-foreground-900">
                        {comment.userName}
                      </span>
                      <span className="text-xs text-foreground-400">
                        {new Date(comment.createdAt).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-foreground-700 break-words">{comment.content}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteComment(comment.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-accent-50 text-foreground-300 hover:text-accent-600 transition-colors shrink-0 opacity-0 group-hover:opacity-100 mt-0.5"
                    title={t('deleteComment')}
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}